import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField
} from '@mui/material';
import { Line } from 'react-chartjs-2';
import { fetchWithDemoFallback } from '../../../helpers/apiHelper';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ASTITitle from '../../labels/ASTITitle';
import ASTIText from '../../labels/ASTIText';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const START_SALDO = 6500;
const DEFAULT_ANCHOR_MONTH = '2026-06';
const DEFAULT_REAL_SALDO = 6500;

function buildMonthsUntilEnd2027() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const endYear = 2027;
  const endMonth = 12;
  const months = [];

  // Start from January of the current year through December 2027
  let year = currentYear;
  let month = 1;

  while (year < endYear || (year === endYear && month <= endMonth)) {
    months.push(`${year}-${String(month).padStart(2, '0')}`);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }

  return months;
}

function monthLabel(month) {
  const [year, monthNumber] = String(month || '').split('-');
  const parsed = new Date(Number(year), Number(monthNumber) - 1, 1);
  if (Number.isNaN(parsed.getTime())) {
    return month;
  }

  return parsed.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
}

function parseMonthFromDate(value) {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw.slice(0, 7);
  }

  if (/^\d{4}-\d{2}$/.test(raw)) {
    return raw;
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    const [dd, mm, yyyy] = raw.split('/');
    if (dd && mm && yyyy) {
      return `${yyyy}-${mm}`;
    }
  }

  if (/^\d{8}$/.test(raw)) {
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}`;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`;
}

function toNumber(value) {
  if (value === null || value === undefined) {
    return value; // Preserve null/undefined for chart to skip
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function toSignedBudgetValue(value) {
  if (value === '' || value === null || value === undefined) {
    return 0;
  }

  const num = Number(value);
  if (!Number.isFinite(num)) {
    return 0;
  }

  return num;
}

function toMoney(value) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2
  }).format(toNumber(value));
}

function toExcludeFlag(value) {
  return value === true || value === 1 || value === '1' || String(value ?? '').toLowerCase() === 'true';
}

function buildEmptySeries(months) {
  const series = {};
  months.forEach((month) => {
    series[month] = 0;
  });
  return series;
}

function accumulateByMonth(months, source, initial = 0) {
  let running = toNumber(initial);
  return months.reduce((acc, month) => {
    running += toNumber(source[month]);
    acc[month] = running;
    return acc;
  }, {});
}
function rowFor(label, months, source) {
  return {
    key: String(label).replaceAll(' ', '_').toLowerCase(),
    label,
    values: Object.fromEntries(months.map((month) => [month, source[month] ?? null]))
  };
}

function getSeriesColor(label) {
  if (String(label).includes('Income')) {
    return '#2e7d32';
  }

  if (String(label).includes('Expense')) {
    return '#e53935';
  }

  if (String(label).includes('Cumulated Saldo')) {
    return '#f9a825';
  }

  return '#42a5f5';
}

export default function BAFTablePage() {
  const [months, setMonths] = useState(() => buildMonthsUntilEnd2027());
  const [monthOffset, setMonthOffset] = useState(() => {
    const monthList = buildMonthsUntilEnd2027();
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const currentMonthIndex = monthList.indexOf(currentMonth);
    return currentMonthIndex >= 0 ? Math.max(0, currentMonthIndex - 2) : 0;
  });
  const [transactions, setTransactions] = useState([]);
  const [budgetMatrix, setBudgetMatrix] = useState({ months: [], categories: [], values: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [xMin, setXMin] = useState(() => {
    try {
      return localStorage.getItem('bafTable.xMin') || '';
    } catch (e) {
      return '';
    }
  });
  const [xMax, setXMax] = useState(() => {
    try {
      return localStorage.getItem('bafTable.xMax') || '';
    } catch (e) {
      return '';
    }
  });
  const [yMin, setYMin] = useState(() => {
    try {
      return localStorage.getItem('bafTable.yMin') || '';
    } catch (e) {
      return '';
    }
  });
  const [yMax, setYMax] = useState(() => {
    try {
      return localStorage.getItem('bafTable.yMax') || '';
    } catch (e) {
      return '';
    }
  });
  const [anchorMonth, setAnchorMonth] = useState(() => {
    try {
      return localStorage.getItem('bafTable.anchorMonth') || DEFAULT_ANCHOR_MONTH;
    } catch (e) {
      return DEFAULT_ANCHOR_MONTH;
    }
  });
  const [realSaldo, setRealSaldo] = useState(() => {
    try {
      const stored = localStorage.getItem('bafTable.realSaldo');
      return stored ? parseFloat(stored) : DEFAULT_REAL_SALDO;
    } catch (e) {
      return DEFAULT_REAL_SALDO;
    }
  });

  const saveChartBounds = useCallback((xMinVal, xMaxVal, yMinVal, yMaxVal) => {
    try {
      if (xMinVal) localStorage.setItem('bafTable.xMin', xMinVal);
      if (xMaxVal) localStorage.setItem('bafTable.xMax', xMaxVal);
      if (yMinVal) localStorage.setItem('bafTable.yMin', yMinVal);
      if (yMaxVal) localStorage.setItem('bafTable.yMax', yMaxVal);
    } catch (e) {
      // localStorage may fail in some contexts
    }
  }, []);

  const saveBafConfig = useCallback((anchorMonthVal, realSaldoVal) => {
    try {
      localStorage.setItem('bafTable.anchorMonth', anchorMonthVal);
      localStorage.setItem('bafTable.realSaldo', String(realSaldoVal));
    } catch (e) {
      // localStorage may fail in some contexts
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [txResponse, budgetResponse] = await Promise.all([
        fetchWithDemoFallback(`${API_BASE}/api/transactions`, {
          method: 'GET',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' }
        }),
        fetchWithDemoFallback(`${API_BASE}/api/budgets`, {
          method: 'GET',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' }
        })
      ]);

      if (!txResponse.ok) {
        throw new Error(`Failed to fetch transactions (HTTP ${txResponse.status})`);
      }
      if (!budgetResponse.ok) {
        throw new Error(`Failed to fetch budgets (HTTP ${budgetResponse.status})`);
      }

      const [txData, budgetData] = await Promise.all([txResponse.json(), budgetResponse.json()]);

      setTransactions(Array.isArray(txData) ? txData : []);
      setBudgetMatrix({
        months: Array.isArray(budgetData?.months) ? budgetData.months : [],
        categories: Array.isArray(budgetData?.categories) ? budgetData.categories : [],
        values: budgetData?.values && typeof budgetData.values === 'object' ? budgetData.values : {}
      });

      const candidateMonths = buildMonthsUntilEnd2027();
      setMonths(candidateMonths);
    } catch (e) {
      setTransactions([]);
      setBudgetMatrix({ months: [], categories: [], values: {} });
      setError(e?.message || 'Unable to load matrix data.');
    } finally {
      setLoading(false);
    }
  }, []);
  const monthsPerPage = 6;

  const visibleMonths = useMemo(() => {
    return months.slice(monthOffset, monthOffset + monthsPerPage);
  }, [months, monthOffset, monthsPerPage]);

  const canNavigateBack = monthOffset > 0;
  const canNavigateForward = monthOffset + monthsPerPage < months.length;

  const handlePrevMonths = useCallback(() => {
    setMonthOffset(Math.max(0, monthOffset - 1));
  }, [monthOffset]);

  const handleNextMonths = useCallback(() => {
    setMonthOffset(Math.min(Math.max(0, months.length - monthsPerPage), monthOffset + 1));
  }, [monthOffset, months.length, monthsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const matrix = useMemo(() => {
    const monthList = months;

    const budgetIncome = buildEmptySeries(monthList);
    const budgetExpense = buildEmptySeries(monthList);

    (budgetMatrix.categories || []).forEach((category) => {
      monthList.forEach((month) => {
        const amount = toSignedBudgetValue(budgetMatrix?.values?.[category]?.[month]);
        if (amount >= 0) {
          budgetIncome[month] += amount;
        } else {
          budgetExpense[month] += Math.abs(amount);
        }
      });
    });

    const budgetSaldo = monthList.reduce((acc, month) => {
      acc[month] = budgetIncome[month] - budgetExpense[month];
      return acc;
    }, {});
    const budgetCumSaldo = accumulateByMonth(monthList, budgetSaldo, START_SALDO);

    const actualIncome = buildEmptySeries(monthList);
    const actualExpense = buildEmptySeries(monthList);

    (transactions || []).forEach((tx) => {
      const autoExclude = toExcludeFlag(tx?.auto_exclude);
      const manualExclude = toExcludeFlag(tx?.manual_exclude);
      if (autoExclude || manualExclude) {
        return;
      }

      const month = parseMonthFromDate(tx?.transactiondate_iso || tx?.transactiondate);
      if (!month || !Object.prototype.hasOwnProperty.call(actualIncome, month)) {
        return;
      }

      const amount = toNumber(tx?.amount);
      const division = String(tx?.division ?? '').trim();
      const isIncomeCategory = division.startsWith('50 - Income') || division.startsWith('50-Income');
      
      if (isIncomeCategory) {
        actualIncome[month] += amount;
      } else {
        actualExpense[month] += amount;
      }
    });

    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    
    // Get previous month for forecast continuity
    const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const previousMonth = `${prevMonthDate.getFullYear()}-${String(prevMonthDate.getMonth() + 1).padStart(2, '0')}`;

    // For current and future months, set actual values to null (no data to show)
    const actualIncomeSeries = monthList.reduce((acc, month) => {
      acc[month] = month < currentMonth ? actualIncome[month] : null;
      return acc;
    }, {});
    const actualExpenseSeries = monthList.reduce((acc, month) => {
      acc[month] = month < currentMonth ? Math.abs(actualExpense[month]) : null;
      return acc;
    }, {});

    const actualSaldo = monthList.reduce((acc, month) => {
      if (month < currentMonth) {
        acc[month] = actualIncomeSeries[month] - actualExpenseSeries[month];
      } else {
        acc[month] = null;
      }
      return acc;
    }, {});
    const actualCumSaldo = monthList.reduce((acc, month) => {
      if (month < currentMonth) {
        // Anchor to user-specified month with user-specified saldo
        if (month === anchorMonth) {
          acc[month] = realSaldo;
        } else if (month > anchorMonth) {
          const prevMonth = monthList[monthList.indexOf(month) - 1];
          acc[month] = (acc[prevMonth] || realSaldo) + (actualSaldo[month] || 0);
        } else {
          // Months before anchor: leave null or calculate backwards
          acc[month] = null;
        }
      } else {
        acc[month] = null;
      }
      return acc;
    }, {});

    // Calculate individual monthly ratios for each month
    const incomeRatioByMonth = monthList.reduce((acc, month) => {
      const actualInc = actualIncome[month] || 0;
      const budgetInc = budgetIncome[month] || 0;
      acc[month] = budgetInc !== 0 ? actualInc / budgetInc : 1;
      return acc;
    }, {});

    const expenseRatioByMonth = monthList.reduce((acc, month) => {
      const actualExp = Math.abs(actualExpense[month] || 0);
      const budgetExp = Math.abs(budgetExpense[month] || 0);
      acc[month] = budgetExp !== 0 ? actualExp / budgetExp : 1;
      return acc;
    }, {});

    // Use the average of last 3 months' ratios for forecasting
    const previousMonthIndex = monthList.indexOf(previousMonth);
    const last3Months = monthList.slice(Math.max(0, previousMonthIndex - 1), previousMonthIndex + 1).filter(m => m < currentMonth);
    
    const incomeRatio = last3Months.length > 0 
      ? last3Months.reduce((sum, month) => sum + incomeRatioByMonth[month], 0) / last3Months.length
      : 1;
    const expenseRatio = last3Months.length > 0
      ? last3Months.reduce((sum, month) => sum + expenseRatioByMonth[month], 0) / last3Months.length
      : 1;

    const forecastIncome = monthList.reduce((acc, month) => {
      if (month === previousMonth) {
        acc[month] = actualIncome[month];
      } else if (month < currentMonth) {
        acc[month] = null;
      } else {
        // Apply ratio to budget for current and future months
        acc[month] = (budgetIncome[month] || 0) * incomeRatio;
      }
      return acc;
    }, {});
    const forecastExpense = monthList.reduce((acc, month) => {
      if (month === previousMonth) {
        acc[month] = Math.abs(actualExpense[month]);
      } else if (month < currentMonth) {
        acc[month] = null;
      } else {
        // Apply ratio to budget for current and future months
        acc[month] = Math.abs((budgetExpense[month] || 0) * expenseRatio);
      }
      return acc;
    }, {});
    const forecastSaldo = monthList.reduce((acc, month) => {
      if (month === previousMonth) {
        acc[month] = actualSaldo[month];
      } else if (month < currentMonth) {
        acc[month] = null;
      } else {
        acc[month] = forecastIncome[month] - forecastExpense[month];
      }
      return acc;
    }, {});
    const forecastCumSaldo = monthList.reduce((acc, month) => {
      if (month === previousMonth) {
        acc[month] = actualCumSaldo[month];
      } else if (month < currentMonth) {
        acc[month] = null;
      } else {
        const prev = month === monthList[0] ? realSaldo : acc[monthList[monthList.indexOf(month) - 1]];
        acc[month] = (prev || realSaldo) + (forecastSaldo[month] || 0);
      }
      return acc;
    }, {});
    const incomeRatioSeries = monthList.reduce((acc, month) => {
      if (month < currentMonth) {
        // Past months: show individual month's accuracy
        acc[month] = incomeRatioByMonth[month] * 100;
      } else {
        // Current and future months: show 3-month average accuracy
        acc[month] = incomeRatio * 100;
      }
      return acc;
    }, {});
    const expenseRatioSeries = monthList.reduce((acc, month) => {
      if (month < currentMonth) {
        // Past months: show individual month's accuracy
        acc[month] = expenseRatioByMonth[month] * 100;
      } else {
        // Current and future months: show 3-month average accuracy
        acc[month] = expenseRatio * 100;
      }
      return acc;
    }, {});
    const rows = [
      rowFor('Budget Income', monthList, budgetIncome),
      rowFor('Budget Expense', monthList, budgetExpense),
      rowFor('Budget Saldo', monthList, budgetSaldo),
      rowFor('Budget Cumulated Saldo', monthList, budgetCumSaldo),
      rowFor('Actual Income', monthList, actualIncomeSeries),
      rowFor('Actual Expense', monthList, actualExpenseSeries),
      rowFor('Actual Saldo', monthList, actualSaldo),
      rowFor('Actual Cumulated Saldo', monthList, actualCumSaldo),
      rowFor('Budget Accuracy Income', monthList, incomeRatioSeries),
      rowFor('Budget Accuracy Expense', monthList, expenseRatioSeries),
      rowFor('Forecast Income', monthList, forecastIncome),
      rowFor('Forecast Expense', monthList, forecastExpense),
      rowFor('Forecast Saldo', monthList, forecastSaldo),
      rowFor('Forecast Cumulated Saldo', monthList, forecastCumSaldo)
    ];

    const chartData = {
      labels: monthList.map(monthLabel),
      datasets: rows.map((row) => {
        // Actual = solid, Budget = dotted, Forecast = dashed
        const isBudget = String(row.label).includes('Budget');
        const isActual = String(row.label).includes('Actual');
        const isForecast = String(row.label).includes('Forecast');
        
        let borderDash = [5, 5]; // Default
        
        if (isActual) {
          borderDash = []; // Solid line
        } else if (isBudget) {
          borderDash = [2, 4]; // Dotted pattern
        } else if (isForecast) {
          borderDash = [5, 5]; // Dashed pattern
        }
        
        return {
          label: row.label,
          data: monthList.map((month) => toNumber(row.values[month])),
          borderColor: getSeriesColor(row.label),
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: borderDash,
          pointRadius: 1,
          tension: 0.2
        };
      })
    };

    return { rows, chartData };
  }, [budgetMatrix, months, transactions, anchorMonth, realSaldo]);
  return (
    <Box
      width="100vw"
      minHeight="100vh"
      padding="20px"
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start"
      sx={{ backgroundColor: '#FFFFFF', color: '#2B1810', overflowY: 'auto', overflowX: 'hidden' }}
    >
      <Box sx={{ width: '100%', maxWidth: '100%', display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <ASTITitle text="Budget / Actual / Forecast" />
            <ASTIText text="12-row monthly matrix through December 2027 with line chart." />
            <ASTIText text={`Start Saldo at end of ${anchorMonth} is ${realSaldo} EUR (configurable below)`} />
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="contained" onClick={fetchData} sx={{ backgroundColor: '#C47A30' }} disabled={loading}>
              Refresh
            </Button>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box display="flex" alignItems="center" gap={2} sx={{ color: '#2B1810', mt: 3 }}>
            <CircularProgress size={20} />
            Loading matrix...
          </Box>
        )}

        {!loading && !error && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', mt: 2, mb: 2 }}>
              <Button
                variant="outlined"
                onClick={handlePrevMonths}
                disabled={!canNavigateBack}
                sx={{ textTransform: 'none' }}
              >
                ← Previous
              </Button>
              <Box sx={{ minWidth: '200px', textAlign: 'center', color: '#2B1810' }}>
                {visibleMonths.length > 0 && (
                  <span>
                    {visibleMonths[0]} to {visibleMonths[visibleMonths.length - 1]}
                  </span>
                )}
              </Box>
              <Button
                variant="outlined"
                onClick={handleNextMonths}
                disabled={!canNavigateForward}
                sx={{ textTransform: 'none' }}
              >
                Next →
              </Button>
            </Box>
            <Box sx={{ mt: 2, p: 2, border: '1px solid #D4B896', borderRadius: '8px', backgroundColor: '#FBF5EC' }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* BAF Configuration */}
                <Box sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#2B1810' }}>BAF:</Box>
                <TextField
                  label="Anchor Month (YYYY-MM)"
                  size="small"
                  value={anchorMonth}
                  onChange={(e) => setAnchorMonth(e.target.value)}
                  placeholder="e.g., 2026-06"
                  sx={{ width: '130px' }}
                />
                <TextField
                  label="Real Saldo (EUR)"
                  size="small"
                  type="number"
                  value={realSaldo}
                  onChange={(e) => setRealSaldo(parseFloat(e.target.value) || 0)}
                  placeholder="e.g., 6500"
                  sx={{ width: '130px' }}
                />
                <Button
                  variant="contained"
                  onClick={() => saveBafConfig(anchorMonth, realSaldo)}
                  sx={{ backgroundColor: '#C47A30', height: '40px' }}
                >
                  Save
                </Button>

                {/* Divider */}
                <Box sx={{ width: '1px', height: '30px', backgroundColor: '#D4B896', mx: 1 }} />

                {/* Chart X-Axis Bounds */}
                <Box sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#2B1810' }}>X-Axis:</Box>
                <TextField
                  label="Min (YYYY-MM)"
                  size="small"
                  value={xMin}
                  onChange={(e) => setXMin(e.target.value)}
                  placeholder="2026-01"
                  sx={{ width: '130px' }}
                />
                <TextField
                  label="Max (YYYY-MM)"
                  size="small"
                  value={xMax}
                  onChange={(e) => setXMax(e.target.value)}
                  placeholder="2027-12"
                  sx={{ width: '130px' }}
                />

                {/* Divider */}
                <Box sx={{ width: '1px', height: '30px', backgroundColor: '#D4B896', mx: 1 }} />

                {/* Chart Y-Axis Bounds */}
                <Box sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#2B1810' }}>Y-Axis:</Box>
                <TextField
                  label="Min"
                  size="small"
                  value={yMin}
                  onChange={(e) => setYMin(e.target.value)}
                  placeholder="0"
                  sx={{ width: '80px' }}
                />
                <TextField
                  label="Max"
                  size="small"
                  value={yMax}
                  onChange={(e) => setYMax(e.target.value)}
                  placeholder="10000"
                  sx={{ width: '80px' }}
                />
                <Button
                  variant="contained"
                  onClick={() => saveChartBounds(xMin, xMax, yMin, yMax)}
                  sx={{ backgroundColor: '#C47A30', height: '40px' }}
                >
                  Save
                </Button>
              </Box>
            </Box>
            <Box sx={{ mt: 2, border: '1px solid #D4B896', borderRadius: '8px', overflow: 'hidden' }}>
              <TableContainer sx={{ maxHeight: 520, overflow: 'auto' }}>
                <Table size="small" stickyHeader sx={{ backgroundColor: '#FBF5EC', color: '#2B1810', minWidth: 1400 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          color: '#2B1810',
                          backgroundColor: '#F0E3D0',
                          borderColor: '#D4B896',
                          position: 'sticky',
                          left: 0,
                          zIndex: 3,
                          minWidth: 280
                        }}
                      >
                        Metric
                      </TableCell>
                      {visibleMonths.map((month) => (
                        <TableCell
                          key={month}
                          sx={{
                            fontWeight: 'bold',
                            color: '#2B1810',
                            backgroundColor: '#F0E3D0',
                            borderColor: '#D4B896',
                            minWidth: 130,
                            whiteSpace: 'nowrap',
                            textAlign: 'right'
                          }}
                        >
                          {monthLabel(month)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {matrix.rows.map((row) => (
                      <TableRow key={row.key} sx={{ '& td': { borderColor: '#D4B896' }, '&:hover': { backgroundColor: '#F0E3D0' } }}>
                        <TableCell
                          sx={{
                            color: getSeriesColor(row.label),
                            position: 'sticky',
                            left: 0,
                            zIndex: 2,
                            backgroundColor: '#FFFFFF',
                            minWidth: 280,
                            fontWeight: 600
                          }}
                        >
                          {row.label}
                        </TableCell>
                        {visibleMonths.map((month) => {
                          const isAccuracyRow = String(row.label).includes('Budget Accuracy');
                          const value = row.values[month];
                          const displayValue = value === null ? '' : isAccuracyRow ? `${value.toFixed(2)}%` : toMoney(value);
                          return (
                            <TableCell key={`${row.key}__${month}`} sx={{ color: getSeriesColor(row.label), textAlign: 'right', minWidth: 130 }}>
                              {displayValue}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box sx={{ mt: 3, border: '1px solid #D4B896', borderRadius: '8px', backgroundColor: '#FBF5EC', p: 2 }}>
              <Box sx={{ height: 420 }}>
                <Line
                  data={matrix.chartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: {
                      legend: { labels: { color: '#2B1810' } },
                      title: {
                        display: true,
                        text: 'Budget / Actual / Forecast Lines',
                        color: '#2B1810'
                      }
                    },
                    scales: {
                      x: {
                        ticks: { color: '#2B1810' },
                        grid: { color: 'rgba(43,24,16,0.12)' },
                        min: xMin || undefined,
                        max: xMax || undefined
                      },
                      y: {
                        ticks: { color: '#2B1810' },
                        grid: { color: 'rgba(43,24,16,0.12)' },
                        min: yMin ? Number(yMin) : undefined,
                        max: yMax ? Number(yMax) : undefined
                      }
                    }
                  }}
                />
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
